import { z } from 'zod';
import { apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { envelopeSchema, locationModelDetailSchema } from '../lib/schemas';

export interface LocationModel {
  id: string;
  name: string;
  location_id: string;
  date_of_year: string;
  not_working_percentage: number;
  location?: {
    id: string;
    name: string;
    office_head?: string;
  };
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreateLocationModelRequest {
  name: string;
  location_id: string;
  date_of_year: string;
  not_working_percentage: number;
}

export interface UpdateLocationModelRequest {
  name: string;
  location_id: string;
  date_of_year: string;
  not_working_percentage: number;
}

const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const locationModelService = {
  async getLocationModels(locationId?: string): Promise<LocationModel[]> {
    if (locationId && !isValidUUID(locationId)) {
      throw new Error(`Invalid location id: ${locationId}`);
    }

    const url = locationId ? apiUrl.location(locationId, '/models') : apiUrl.locationModels();
    const result = await apiClient.get(url, {
      validate: envelopeSchema('location_models', z.array(locationModelDetailSchema)),
      deduplicate: true,
      context: 'getLocationModels',
    });

    return result.location_models;
  },

  async getLocationModel(id: string): Promise<LocationModel> {
    const result = await apiClient.get(`${apiUrl.locationModels()}/${id}`, {
      validate: envelopeSchema('location_model', locationModelDetailSchema),
      deduplicate: true,
      context: 'getLocationModel',
    });
    return result.location_model;
  },

  async getLocationModelByLocationAndDate(
    locationId: string,
    date: string
  ): Promise<LocationModel> {
    const result = await apiClient.get(
      `${apiUrl.location(locationId, '/models/date')}?date=${date}`,
      {
        validate: envelopeSchema('location_model', locationModelDetailSchema),
        deduplicate: true,
        context: 'getLocationModelByLocationAndDate',
      }
    );
    return result.location_model;
  },

  async createLocationModel(model: CreateLocationModelRequest): Promise<LocationModel> {
    const result = await apiClient.post(apiUrl.locationModels(), model, {
      validate: envelopeSchema('location_model', locationModelDetailSchema),
      context: 'createLocationModel',
    });
    return result.location_model;
  },

  async updateLocationModel(id: string, model: UpdateLocationModelRequest): Promise<LocationModel> {
    const result = await apiClient.put(`${apiUrl.locationModels()}/${id}`, model, {
      validate: envelopeSchema('location_model', locationModelDetailSchema),
      context: 'updateLocationModel',
    });
    return result.location_model;
  },

  async deleteLocationModel(id: string): Promise<void> {
    await apiClient.delete(`${apiUrl.locationModels()}/${id}`, {
      context: 'deleteLocationModel',
    });
  },
};
